import { Component, Prop, h, State, Element, Build, Host } from '@stencil/core';

const COMPONENT_NAME = 'sn-video-type-1';

/**
 * Video section component for SignNow integration and product pages.
 * Displays a centered heading and description above a full-width YouTube video player
 * with a custom thumbnail overlay and a click-to-play interaction. The video container
 * fades in from below when scrolled into the viewport (IntersectionObserver-based).
 *
 * The thumbnail is replaced with the YouTube iframe on user interaction; autoplay is
 * enabled when loading from the preview state. Supports optional custom marketing
 * thumbnails via the thumbnail-* props; falls back to YouTube's auto-generated image.
 *
 * Card descriptions (heading, description) are slotted so translated text is injected
 * by Herald before SSR and renders correctly on localised pages.
 *
 * @product signnow
 * @category SignNow
 * @slot heading - {required}, <me-heading> - Section heading above the video player
 * @slot description - <me-text> - Section description below the heading
 * @example
 *   <sn-video-type-1
 *     video-id="_rww3X16RvI"
 *     video-title="See how easy it is to transform your lease renewal workflows"
 *     thumbnail-src="https://marketing-static.signnow.com/386/images/integrations/video-integration.png"
 *     thumbnail-srcset="https://marketing-static.signnow.com/386/images/integrations/video-integration.png 1x, https://marketing-static.signnow.com/386/images/integrations/video-integration@2x.png 2x"
 *     thumbnail-webp-src="https://marketing-static.signnow.com/386/images/integrations/video-integration.webp"
 *     thumbnail-webp-srcset="https://marketing-static.signnow.com/386/images/integrations/video-integration.webp 1x, https://marketing-static.signnow.com/386/images/integrations/video-integration@2x.webp 2x"
 *   >
 *     <me-heading slot="heading" variant="2" tag="h2" align="center">See how easy it is to transform your lease renewal workflows</me-heading>
 *     <me-text slot="description" align="center">Watch the integration in action and see how you can start effortlessly signing agreements.</me-text>
 *   </sn-video-type-1>
 */
@Component({
  tag: 'sn-video-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnVideoType1 {
  @Element() el!: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * YouTube video ID (the part after `?v=` in the URL).
   */
  @Prop() videoId!: string;

  /**
   * Accessible title for the embedded video iframe. Read by screen readers in place of visual content.
   * @content
   */
  @Prop({ attribute: 'video-title' }) videoTitle!: string;

  /**
   * Optional custom thumbnail image URL (PNG/JPG). Falls back to the YouTube auto-generated thumbnail when omitted.
   * @mediaType image
   */
  @Prop({ attribute: 'thumbnail-src' }) thumbnailSrc?: string;

  /**
   * Optional srcset for the custom thumbnail. Provide both a 1x and a 2x candidate with density descriptors so standard screens don't download the heavier retina asset, e.g. `image.png 1x, image@2x.png 2x`.
   * @mediaType image
   */
  @Prop({ attribute: 'thumbnail-srcset' }) thumbnailSrcSet?: string;

  /**
   * Optional WebP version of the custom thumbnail for modern browsers. Must point to a real `.webp` file — a PNG/JPG here defeats the WebP optimisation and ships the larger original.
   * @mediaType image
   */
  @Prop({ attribute: 'thumbnail-webp-src' }) thumbnailWebpSrc?: string;

  /**
   * Optional srcset for the WebP custom thumbnail. Provide both a 1x and a 2x `.webp` candidate with density descriptors, e.g. `image.webp 1x, image@2x.webp 2x`.
   * @mediaType image
   */
  @Prop({ attribute: 'thumbnail-webp-srcset' }) thumbnailWebpSrcSet?: string;

  @State() private playing: boolean = false;

  private observer?: IntersectionObserver;

  componentDidLoad() {
    if (!Build.isBrowser) return;
    this.initFadeAnimation();
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    this.observer?.disconnect();
  }

  private initFadeAnimation() {
    const wrapper = this.el.querySelector<HTMLElement>(`.${COMPONENT_NAME}__video-wrapper`);
    if (!wrapper) return;

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            wrapper.classList.add('is-visible');
            this.observer?.disconnect();
          }
        },
        { threshold: 0.1 },
      );
      this.observer.observe(wrapper);
    } else {
      wrapper.classList.add('is-visible');
    }
  }

  private handlePlay = () => {
    this.playing = true;
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handlePlay();
    }
  };

  private get thumbnailImgSrc(): string {
    return this.thumbnailSrc ?? `https://img.youtube.com/vi/${this.videoId}/maxresdefault.jpg`;
  }

  private renderPreview() {
    const hasWebp = !!(this.thumbnailWebpSrc || this.thumbnailWebpSrcSet);

    return (
      <div class={`${COMPONENT_NAME}__player ${COMPONENT_NAME}__player--preview`} aria-live="polite">
        <picture>
          {hasWebp && <source type="image/webp" srcSet={this.thumbnailWebpSrcSet || this.thumbnailWebpSrc} />}
          <img
            class={`${COMPONENT_NAME}__thumbnail`}
            src={this.thumbnailImgSrc}
            srcSet={this.thumbnailSrcSet}
            alt=""
            onError={e => {
              const img = e.currentTarget as HTMLImageElement;
              if (!this.thumbnailSrc) {
                img.src = `https://img.youtube.com/vi/${this.videoId}/hqdefault.jpg`;
              }
            }}
          />
        </picture>
        <button class={`${COMPONENT_NAME}__play-btn`} type="button" aria-label="Play video" tabIndex={0} onClick={this.handlePlay} onKeyDown={this.handleKeyDown}>
          <svg class={`${COMPONENT_NAME}__play-icon`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 94 94" width="94" height="94" aria-hidden="true" focusable="false">
            <g fill="none">
              <circle class={`${COMPONENT_NAME}__play-circle`} cx="47" cy="47" r="47" />
              <path
                class={`${COMPONENT_NAME}__play-triangle`}
                d="M62.078 48.21 39.192 62.62A1.43 1.43 0 0 1 37 61.41V32.59a1.43 1.43 0 0 1 2.192-1.21l22.886 14.41a1.43 1.43 0 0 1 0 2.42"
              />
            </g>
          </svg>
        </button>
      </div>
    );
  }

  private renderPlayer() {
    return (
      <div class={`${COMPONENT_NAME}__player`} aria-live="polite">
        <iframe
          class={`${COMPONENT_NAME}__iframe`}
          src={`https://www.youtube.com/embed/${this.videoId}?autoplay=1`}
          title={this.videoTitle}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <me-section class={COMPONENT_NAME} yGap="2xl" yGapLg="4xl">
          <div class={`${COMPONENT_NAME}__intro`}>
            <slot name="heading" />
            <slot name="description" />
          </div>
          <div class={`${COMPONENT_NAME}__video-wrapper`}>{this.playing ? this.renderPlayer() : this.renderPreview()}</div>
        </me-section>
      </Host>
    );
  }
}
